import React, { useCallback } from 'react';
import { Input, InputGroup } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslate } from 'ts/common/hooks';

interface CreditCardNumberInputProps {
    name: string;
    onCardTypeChange?: (cardType: string | null) => void;
}

type CardType = 'visa' | 'mastercard' | 'amex' | 'discover' | null;

const LegacyCreditCardNumberInput: React.FC<CreditCardNumberInputProps> = ({
    name,
    onCardTypeChange
}) => {
    const { register, setValue, watch } = useFormContext();
    const t = useTranslate('clientInvoice.creditCard');
    const value = watch(name) || '';

    const detectCardType = useCallback((cardNumber: string): CardType => {
        const cleanNumber = cardNumber.replace(/\s/g, '');

        if (/^4/.test(cleanNumber)) {
            return 'visa';
        }

        if (/^5[1-5]/.test(cleanNumber)) {
            return 'mastercard';
        }

        if (/^3[47]/.test(cleanNumber)) {
            return 'amex';
        }

        if (/^6(?:011|5)/.test(cleanNumber)) {
            return 'discover';
        }

        return null;
    }, []);

    const formatCardNumber = useCallback((value: string, cardType: CardType): string => {
        const cleanValue = value.replace(/\D/g, '');

        if (!cardType) {
            return cleanValue;
        }

        switch (cardType) {
            case 'amex':
                return cleanValue
                    .match(/^([0-9]{1,4})?([0-9]{1,6})?([0-9]{1,5})?/)!
                    .slice(1, 4)
                    .join(' ')
                    .trim();
            default:
                const matched = cleanValue.match(/^([0-9]{1,4}){1,4}/g);

                if (!matched) {
                    return cleanValue;
                }

                return matched[0].match(/[0-9]{1,4}/g)!.join(' ');
        }
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            const detectedType = detectCardType(inputValue);
            const formattedValue = formatCardNumber(inputValue, detectedType);

            setValue(name, formattedValue);
            onCardTypeChange?.(detectedType);
        },
        [name, setValue, formatCardNumber, detectCardType, onCardTypeChange]
    );

    const validateCardNumber = useCallback(
        (value: string) => {
            const cleanValue = value.replace(/\s/g, '');

            if (!cleanValue) {
                return false;
            }

            if (cleanValue.length !== 15 && cleanValue.length !== 16) {
                return t('invalidCardLength');
            }

            const arr: string[] = [];
            const dblarr: number[] = [];
            let dbl = false;
            let sum = 0;

            for (let i = 0; i < cleanValue.length; i++) {
                arr.push(cleanValue.slice(i, i + 1));
            }

            for (let i = arr.length - 1; i >= 0; i--) {
                if (dbl) {
                    dblarr.push(parseInt(arr[i]) * 2);
                    dbl = false;
                } else {
                    dblarr.push(parseInt(arr[i]));
                    dbl = true;
                }
            }

            for (let i = 0; i < dblarr.length; i++) {
                let incVal: number;

                if (dblarr[i] >= 10) {
                    incVal = dblarr[i] - 9;
                } else {
                    incVal = dblarr[i];
                }

                sum += parseInt(incVal.toString(), 10);
            }

            if (sum % 10) {
                return t('invalidCardNumber');
            }

            return true;
        },
        [t]
    );

    return (
        <InputGroup>
            <Input
                {...register(name, {
                    required: true,
                    validate: validateCardNumber
                })}
                value={value}
                onChange={handleInputChange}
                maxLength={19} // Amex format: 4-6-5 = 15 chars + 2 spaces = 17, others: 4-4-4-4 = 16 chars + 3 spaces = 19
                type="text"
                inputMode="numeric"
                pattern="[0-9\s]*"
            />
        </InputGroup>
    );
};

export default LegacyCreditCardNumberInput;
